/** @jsx jsx */
/* eslint-disable no-use-before-define */
import React, { useMemo, useState } from 'react'
import { jsx } from '@emotion/core'
import { format, mediaQueries, isEmpty, domAttrSet, Loading, Flyout } from '../twd'
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import useChart from './use-chart'
import { displayInline } from './style'

const yieldCurveTooltip = `
    A yield curve is a type of line graph that compares yields of bonds (of equal credit quality)
    across a range of maturity dates.  These points are plotted on the graph and connected with a line.
    Investors can think of these yield curves as expressing how much it costs an issuer to borrow
    money for a stated term. This US Treasury Yield Curve chart is a visual representation of short,
    medium, and long-term rates for US treasuries. As treasury bonds are backed by the full faith and
    credit of the US government, the US Treasury Yield Curve is often referred to as the “risk-free” benchmark
    curve. Investors often compare the US Treasury Curve against the yield curve of other (riskier) assets
    to analyze yield spread and assess relative value. This US Treasury Yield curve chart presents
    the current (as of the last trading session’s close), 1-month, 6-month, and 1-year historical curves.
`

export const Chart = ({ headerLabel="US Yield Curve", headerClassName="btn-label btn-flyout text-bold"} = {}) => {
    const { chartData, chartDataStatus } = useChart()
    const [keyboardIndex, setKeyboardIndex] = useState(0)
    const [isKeyboardFocus, setIsKeyboardFocus] = useState(false)
    const [interactionSource, setInteractionSource] = useState('keyboard')

    const keyboardPoint = useMemo(() => {
        if (!isKeyboardFocus || !Array.isArray(chartData) || !chartData.length) return null
        const point = chartData[Math.min(keyboardIndex, chartData.length - 1)]
        if (!point) return null
        return { x: point.x }
    }, [chartData, keyboardIndex, isKeyboardFocus])

    const onChartKeyDown = (e) => {
        if (!Array.isArray(chartData) || !chartData.length) return
        if (e.key === 'ArrowRight') {
            e.preventDefault()
            setInteractionSource('keyboard')
            setKeyboardIndex((i) => Math.min(i + 1, chartData.length - 1))
        } else if (e.key === 'ArrowLeft') {
            e.preventDefault()
            setInteractionSource('keyboard')
            setKeyboardIndex((i) => Math.max(i - 1, 0))
        }
    }

    const keyboardTooltipPoint = isKeyboardFocus && interactionSource === 'keyboard' ? keyboardPoint : null

    return <React.Fragment>
        <div className="row vertical-offset-xs"></div>


        <Loading status={chartDataStatus}>

        <div className="row vertical-offset-xs" style={{marginLeft:'10px'}}>
            <Flyout
                label={headerLabel}
                className={headerClassName}
                html={yieldCurveTooltip}
            />
        </div>
        <div className="row vertical-offset-sm">
        <div className="col-md-12 col-xs-12">
        { Array.isArray(chartData) && (
            <div
                tabIndex={0}
                role="group"
                aria-label="Yield curve scatter chart. Use left and right arrow keys to move across maturities."
                onFocus={() => {
                    setIsKeyboardFocus(true)
                    setInteractionSource('keyboard')
                    setKeyboardIndex(0)
                }}
                onBlur={() => setIsKeyboardFocus(false)}
                onMouseMove={() => setInteractionSource('mouse')}
                onKeyDown={onChartKeyDown}
            >
            <ResponsiveContainer width="100%" height={300}>
            <ScatterChart
                accessibilityLayer={true}
                margin={{
                    top: 5, right: 30, left: 10, bottom: 5,
                }}
            >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />

                <XAxis
                    dataKey="x"
                    domain={[0,10]}
                    padding={{left:10, right:10}}
                    type="number"
                    scale="linear"
                    tickFormatter={(i) => formatXAxisTicks(chartData, i)}
                />

                <YAxis
                    dataKey="y"
                    domain={['auto', 'auto']}
                    padding={{top:10, bottom:10}}
                    width={15}
                    scale='linear'
                    orientation='right'
                    tickFormatter={formatYAxisTicks}
                />

                <Legend
                    verticalAlign='top'
                    align='left'
                    content={renderLegend}
                />

                <Scatter name="current"
                    data={getData(chartData, 'current', '#8b06b9')}
                    line={{strokeWidth:3}}
                    fill="#8b06b9"
                    shape={(props) => renderShape(props, keyboardTooltipPoint)} />
                <Scatter name="1mo"
                    data={getData(chartData, '1mo', '#ef6f06')}
                    line fill="#ef6f06"
                    shape={(props) => renderShape(props, keyboardTooltipPoint)} />
                <Scatter name="6mo"
                    data={getData(chartData, '6mo', '#1192e9')}
                    line fill="#1192e9"
                    shape={(props) => renderShape(props, keyboardTooltipPoint)} />
                <Scatter name="1yr"
                    data={getData(chartData, '1yr', '#00a3a0')}
                    line fill="#00a3a0"
                    shape={(props) => renderShape(props, keyboardTooltipPoint)} />

                <Tooltip
                    active={keyboardTooltipPoint ? true : undefined}
                    position={{y:25}}
                    offset={1}
                    content={<TooltipContent {...{chartData, keyboardPoint: keyboardTooltipPoint}} />}
                    isAnimationActive={false}
                    cursor={keyboardTooltipPoint ? false : <TooltipCursor />}
                    wrapperStyle={{zIndex:3}}
                    />

            </ScatterChart>
            </ResponsiveContainer>
            </div>
        )}
        {keyboardTooltipPoint && (
            <div aria-live="polite" className="sr-only">
                {formatAriaPoint(chartData, keyboardTooltipPoint.x)}
            </div>
        )}
        </div>
        </div>

        <div className="row vertical-offset-sm">
        <div className="col-md-12 col-xs-12 text-muted" style={{marginLeft:'10px'}}>
            Current yield is indicative of last trade session's closing level.
        </div>
        </div>
        </Loading>
    </React.Fragment>
}


const getData = (data, key, color) => data
    .filter( a => !isEmpty(a[key]))
    .map( a => ({
        key, color, x:a['x'], y:a[key]
    }))

const renderShape = ({cx, cy, x, y, width, height, color, payload}, keyboardPoint) => {
    const isActive = keyboardPoint && payload && payload.x === keyboardPoint.x
    let p = {
        cx, cy, x, y, width, height,
        stroke: color,
        fill: isActive ? color : '#fff',
        r: isActive ? 5 : 4,
        className:'recharts-dot'
    }
    return <circle {...p}
        onMouseOver={(e) => domAttrSet(e.target, {fill:color})}
        onMouseOut={(e) => domAttrSet(e.target, {fill:'#fff'})}
        />
}

const legends = {
    'current': 'Current',
    '1mo': '1M Ago',
    '6mo': '6M Ago',
    '1yr': '1Y Ago'
}

const renderLegend = ({payload}) => (
    <div style={{width:'100%', textAlign:'left', marginBottom:'10px'}}>{
        payload.map((entry, index) => {
            let v = entry.value
            return <span key={index}
                style={{borderLeft:'4px solid ' + entry.color}}
                css={legendCss} >
                &nbsp; {legends[v] || v}
            </span>
        })
    }</div>
)

const legendCss = {
    [mediaQueries.notXs]: {
        paddingRight: '40px'
    },
    [mediaQueries.xs]: {
        paddingRight: '20px'
    }
}


const formatXAxisTicks = (chartData, i) => (chartData[i].name)

const formatYAxisTicks = (x) => (x.toLocaleString("en-US", {minimumFractionDigits:3, maximumFractionDigits:3}))

const TooltipContent = ({payload, active, chartData, keyboardPoint}) => {
    if (!active) return null

    const p = payload && payload[0]
    const a = (p && p.payload) || keyboardPoint
    if (!a) return null

    const b = chartData.filter(({x}) => x === a.x)[0]
    if (!b) return null

    return <div className="custom-tooltip"
        style={{border:'1px solid #e4e4e4', backgroundColor:'#fff', textAlign:'left', padding:'5px'}}>
        <div className="text-bold">{formatTooltipTitle(chartData, a.x)}</div>
        <div css={displayInline}>
        <span style={{borderLeft:'4px solid #8b06b9', paddingLeft:'5px', paddingRight:'5px'}}>Current  <b>{format.ytw3(b['current'])}</b></span>
        <span style={{borderLeft:'4px solid #ef6f06', paddingLeft:'5px', paddingRight:'5px'}}>1M Ago <b>{format.ytw3(b['1mo'])}</b></span>
        </div>
        <span style={{borderLeft:'4px solid #1192e9', paddingLeft:'5px', paddingRight:'5px'}}>6M Ago <b>{format.ytw3(b['6mo'])}</b></span>
        <span style={{borderLeft:'4px solid #00a3a0', paddingLeft:'5px', paddingRight:'5px'}}>1Y Ago <b>{format.ytw3(b['1yr'])}</b></span>
    </div>
}

const formatTooltipTitle = (chartData, i) => {
    const n = chartData[i].name
    return `${parseInt(n, 10)} ${n.endsWith('M') ? 'Month' : 'Year'} Note`
}

const formatAriaPoint = (chartData, i) => {
    const point = chartData[i]
    if (!point) return ''
    return [
        `${formatTooltipTitle(chartData, i)}.`,
        `Current ${format.ytw3(point.current)}.`,
        `1 month ago ${format.ytw3(point['1mo'])}.`,
        `6 month ago ${format.ytw3(point['6mo'])}.`,
        `1 year ago ${format.ytw3(point['1yr'])}.`
    ].join(' ')
}

const TooltipCursor = ({stroke, width, height, x, y, top,}) => {

    if (!x) return null

    let args = {
        x, y, stroke, width, height,
        className: 'recharts-cross recharts-tooltip-cursor',
        pointerEvents: 'none',
        strokeDasharray: '5',
        d: `M${x},${top}v${height}`
    }
    return <path {...args} />
}